import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";

import { getErrors, requiredError } from "shared/utils/validationUtils";

type FormHeadersProps = {
  title: string;
  onHandleDelete?: Function;
  onHandleDiscardChanges?: Function;
  onHandleClearForm?: Function;
  additionalControls?: React.ReactNode;
  displaySubmit?: boolean;
  dirty?: boolean;
  errors?: any;
  touched?: any;
  warnings?: { [key: string]: string };
  apiErrors?: {
    [key: string]: { message: string; openModal?: Function };
  } | null;
  apiWarnings?: {
    [key: string]: { message: string; openModal?: Function };
  } | null;
  forms?: string[];
  formWarnings?: string[];
};

const FormHeaders = ({
  title,
  displaySubmit,
  onHandleDelete,
  onHandleDiscardChanges,
  onHandleClearForm,
  additionalControls,
  dirty,
  errors,
  apiErrors = {},
  touched,
  warnings = {},
  apiWarnings = {},
  forms,
  formWarnings = [],
}: FormHeadersProps) => {
  const apiWarningMessages = Object.entries(apiWarnings || {}).reduce(
    (acc: any, [key, value]) => {
      if (value.message) {
        acc[key] = value.message;
      }
      return acc;
    },
    {}
  );
  const apiErrorMessages = Object.entries(apiErrors || {}).reduce(
    (acc: any, [key, value]) => {
      if (value.message) {
        acc[key] = value.message;
      }
      return acc;
    },
    {}
  );
  const allErrors = getErrors(
    {
      ...errors,
      ...apiErrorMessages,
    },
    touched,
    true,
    forms
  );
  const allWarnings = getErrors(
    {
      ...warnings,
      ...apiWarningMessages,
    },
    touched,
    true,
    formWarnings
  );
  const errorList = Object.values(allErrors).flat();
  const hasNonRequiredErrors = errorList.some((error) => !requiredError(error));
  const hasErrors = errorList.length > 0;
  const hasWarnings = Object.values(allWarnings).length > 0;
  const hasErrorsOrWarnings = hasErrors || hasWarnings;
  const hasErrorsAndWarnings = hasErrors && hasWarnings;
  const buttonString = hasErrorsAndWarnings
    ? "Errors and Warnings"
    : hasErrors
    ? "Errors"
    : "Warnings";

  return (
    <div
      className={classNames("LISForm__sectionHeaderContainer", {
        "LISForm__sectionHeaderContainer--buttonPadding": displaySubmit,
        "LISForm__sectionHeaderContainer--errors":
          hasErrors && hasNonRequiredErrors,
        "LISForm__sectionHeaderContainer--warnings":
          hasWarnings && !hasNonRequiredErrors,
      })}
    >
      <h2 className="LISForm__sectionHeader">
        {title}
        {hasErrorsOrWarnings && (
          <>
            <button
              type="button"
              className={classNames("Button LISForm__errors", {
                "Button--red": hasErrors && hasNonRequiredErrors,
              })}
              data-for={`${title}-tooltip`}
              data-tip=""
              data-event="click"
            >
              {`Show ${buttonString}`}
            </button>
            <ReactTooltip
              id={`${title}-tooltip`}
              place="top"
              effect="solid"
              clickable
              event="click"
              globalEventOff="click"
            >
              {hasErrorsAndWarnings && (
                <h3 className="LISForm__errorsHeader">Errors</h3>
              )}
              {hasErrors && (
                <ul className="LISForm__errorsList">
                  {allErrors &&
                    Object.entries(allErrors).map(([key, error], i) => (
                      <li key={`error-list-item-${i}`}>
                        <span className="LISForm__warningItem">
                          {error}
                          {apiErrors?.[key]?.openModal && (
                            <QuestionMarkCircleIcon
                              onClick={() => apiErrors?.[key]?.openModal?.()}
                              style={{ cursor: "pointer" }}
                              height={20}
                            />
                          )}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
              {hasErrorsAndWarnings && (
                <h3 className="LISForm__errorsHeader">Warnings</h3>
              )}
              {hasWarnings && (
                <ul className="LISForm__errorsList">
                  {allWarnings &&
                    Object.entries(allWarnings).map(([key, warning], i) => (
                      <li key={`warning-list-item-${i}`}>
                        <span className="LISForm__warningItem">
                          {warning}
                          {apiWarnings?.[key]?.openModal && (
                            <QuestionMarkCircleIcon
                              onClick={() => apiWarnings?.[key]?.openModal?.()}
                              style={{ cursor: "pointer" }}
                              height={20}
                            />
                          )}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </ReactTooltip>
          </>
        )}
      </h2>
      <div className="LISForm__sectionHeaderActions">
        {onHandleDelete && (
          <button type="button" onClick={() => onHandleDelete()}>
            Delete
          </button>
        )}
        {onHandleClearForm && (
          <button type="button" onClick={() => onHandleClearForm()}>
            Clear
          </button>
        )}
        {additionalControls}
        {onHandleDiscardChanges && (
          <button
            type="button"
            disabled={!dirty}
            onClick={() => onHandleDiscardChanges()}
          >
            Discard Changes
          </button>
        )}
        {displaySubmit && (
          <button type="submit" disabled={!dirty || hasErrors}>
            Save Changes
          </button>
        )}
      </div>
    </div>
  );
};

export default FormHeaders;
